/**
 * Custom hook to detect unit type ID for selected part
 */
import { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { getAllPartsFromText } from "../utils/partUtils";

export const useUnitTypeDetection = ({
  text,
  selectedPartName,
  allParts,
  currentGlobalIndex,
}) => {
  const [selectedPartUnitTypeId, setSelectedPartUnitTypeId] = useState(null);
  const [unitsUpdateTrigger, setUnitsUpdateTrigger] = useState(0);
  const { units } = useSelector((state) => state.units);
  const { parts } = useSelector((state) => state.parts);

  useEffect(() => {
    const match = selectedPartName?.match(/\((\d+)\)/);
    const extractedPartNumber = match ? match[1] : "";
    const partNumber = selectedPartName?.trim() || "";

    let newUnitTypeId = null;

    // Proceed only if selectedContent is a part
    if (text?.selectedContent?.type === "part") {
      try {
        // 1. Try direct unit_type_id from selectedContent
        if (text.selectedContent.unit_type_id) {
          newUnitTypeId = text.selectedContent.unit_type_id;
        } else if (text.selectedContent.part?.unit_type_id_ref) {
          newUnitTypeId = text.selectedContent.part.unit_type_id_ref;
        } else {
          // 2. Try to find part in Redux `parts` array
          const matchedPart = parts.find(
            (p) =>
              p.part_number &&
              p.part_number.includes(extractedPartNumber) &&
              text.selectedContent?.typeKey === p.type_details?.name
          );

          if (matchedPart?.unit_type_id_ref) {
            newUnitTypeId = matchedPart.unit_type_id_ref;
          } else {
            // 3. Try partsByType again for unit_type_id
            let partRefId = null;
            for (const typeName in text.partsByType) {
              const typeData = text.partsByType[typeName];
              if (typeData?.parts) {
                const foundPart = typeData.parts.find(
                  (p) =>
                    (p.part && p.part.includes(`(${partNumber})`)) ||
                    (p.part && p.part.trim() === selectedPartName.trim())
                );

                if (foundPart) {
                  partRefId = foundPart.part_ref;
                  if (foundPart.unit_type_id) {
                    newUnitTypeId = foundPart.unit_type_id;
                    break;
                  }
                }
              }
            }

            // 4. Try Redux part lookup by ID
            if (newUnitTypeId === null && partRefId) {
              const partById = parts.find((p) => p.part_id === partRefId);
              if (partById?.unit_type_id_ref) {
                newUnitTypeId = partById.unit_type_id_ref;
              }
            }

            // 5. Try to derive unit type from selected unit name
            if (newUnitTypeId === null && text.selectedContent.unit) {
              const unitName = text.selectedContent.unit;
              for (const unitType of units) {
                const matchingUnit = unitType.unit_type_for_name?.find(
                  (u) => u.unit_name === unitName
                );
                if (matchingUnit) {
                  newUnitTypeId = unitType.unit_type_id;
                  break;
                }
              }
            }

            // 6. Try allParts as fallback
            if (newUnitTypeId === null) {
              const currentPart = allParts.find(
                (p) => p.part.trim() === selectedPartName.trim()
              );
              if (currentPart?.unit_type_id) {
                newUnitTypeId = currentPart.unit_type_id;
              }
            }
          }
        }
      } catch (error) {
        newUnitTypeId = null;
      }
    } else {
      newUnitTypeId = null;
    }

    if (newUnitTypeId !== selectedPartUnitTypeId) {
      setSelectedPartUnitTypeId(newUnitTypeId);
    }
  }, [
    text?.selectedContent,
    parts,
    units,
    selectedPartName,
    text?.partsByType,
    currentGlobalIndex,
    allParts,
    selectedPartUnitTypeId,
  ]);

  // Force re-evaluation of getFilteredUnits when selectedPartUnitTypeId changes
  useEffect(() => {
    if (selectedPartUnitTypeId !== null) {
      setUnitsUpdateTrigger((prev) => prev + 1);
    }
  }, [selectedPartUnitTypeId]);

  return { selectedPartUnitTypeId, unitsUpdateTrigger };
};
